import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../components/shared/Sidebar";
import Navbar from "../components/shared/Navbar";
import HelpTooltip from "../components/shared/HelpTooltip";
import TourRestartButton from "../components/shared/TourRestartButton";
import { useProductTour } from "../hooks/useProductTour";
import KanbanBoard from "../components/kanban/KanbanBoard";
import ChatPanel from "../components/chat/ChatPanel";
import api from "../lib/api";
import { useWS } from "../context/WebSocketContext";
import { useAuth } from "../context/AuthContext";
import {
  Plus, TrendingUp, Users, Trophy, Percent,
  GitBranch, Pencil, Trash2, X, ChevronDown, Eye, EyeOff,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

function MetricCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-slate-900/70 border border-slate-800 rounded-xl p-4 flex items-center gap-3 hover:border-slate-700 transition"
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="text-xl font-bold text-slate-50" style={{ fontFamily: "Outfit" }}>{value}</div>
      </div>
    </div>
  );
}

function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [columns, setColumns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [activeLead, setActiveLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [pendingPrefillMessage, setPendingPrefillMessage] = useState(null);

  // Navegação vinda de fora (ex: botão "Abrir chat" num card de Task em
  // /tasks) — abre o lead pelo id e, se houver, leva a mensagem sugerida
  // pro ChatPanel. Roda só uma vez, no mount.
  useEffect(() => {
    const openLeadId = location.state?.openLeadId;
    if (!openLeadId) return;
    const prefillMessage = location.state?.prefillMessage || null;
    (async () => {
      try {
        const { data } = await api.get(`/leads/${openLeadId}`);
        setActiveLead(data);
        if (prefillMessage) setPendingPrefillMessage(prefillMessage);
      } catch (error) {
        toast.error("Não foi possível abrir o lead");
      } finally {
        navigate(location.pathname, { replace: true, state: {} });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Referência estável (setState do useState já é estável entre renders) —
  // evita que o useEffect do ChatPanel que consome isso precise ignorar a
  // regra de exhaustive-deps ou reexecute a cada render do Dashboard.
  const handleInitialPrefillConsumed = useCallback(() => setPendingPrefillMessage(null), []);

  // ── Pipeline state ──────────────────────────────────────────────────────────
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [newPipelineOpen, setNewPipelineOpen] = useState(false);
  const [renamePipeline, setRenamePipeline] = useState(null); // {id, name}
  const selectedPipelineRef = useRef(null);
  // Keep ref in sync so WS callbacks always use the latest pipeline
  useEffect(() => { selectedPipelineRef.current = selectedPipeline; }, [selectedPipeline]);

  // Ganho/Perdido ficam ocultos do kanban por padrão (acumulam muitos leads e
  // deixavam a carga inicial lenta). Estado temporário, não persiste entre
  // reloads. O ref existe pelo mesmo motivo do selectedPipelineRef: os
  // callbacks de WebSocket (refresh()) precisam sempre ler o valor mais
  // recente, não o capturado no closure de quando o listener foi registrado.
  const [showWonLost, setShowWonLost] = useState(false);
  const showWonLostRef = useRef(false);

  // Filtro de tags do kanban (multi-select, lógica OU) -- mesmo padrão de
  // ref do showWonLost: o valor mais recente precisa estar disponível pros
  // callbacks de WebSocket sem depender do closure de quando foram registrados.
  const [selectedTags, setSelectedTags] = useState([]);
  const selectedTagsRef = useRef([]);

  const { on } = useWS();
  const { user } = useAuth();
  const isAdmin = ["owner", "admin", "super_admin"].includes(user?.role);

  // useMemo (em vez de um ternário solto) pra dar uma referência estável
  // entre renders quando columns/leads não mudam -- necessário pro
  // react-hooks/exhaustive-deps aceitar essas variáveis como dependência
  // segura do useCallback de handleLeadMoveOptimistic logo abaixo.
  const safeColumns = useMemo(() => (Array.isArray(columns) ? columns : []), [columns]);
  const safeLeads = useMemo(() => (Array.isArray(leads) ? leads : []), [leads]);
  const hasWonLostColumns = safeColumns.some((c) => c.is_won || c.is_lost);
  // Passado pro KanbanBoard só pra filtrar o que é RENDERIZADO -- o array
  // `columns` continua completo, porque a lógica de reordenar coluna (drag)
  // usa os índices desse array como fonte da verdade e não pode perder as
  // colunas ocultas do meio dele.
  const hiddenColumnIds = showWonLost
    ? new Set()
    : new Set(safeColumns.filter((c) => c.is_won || c.is_lost).map((c) => c.id));

  // ── Tour guiado (primeira visita) ───────────────────────────────────────────
  // Passos condicionais pra só apontar pra elementos que realmente estão na
  // tela nesta sessão (ex: "Novo Lead" some pra agentes, o toggle Ganho/
  // Perdido some se o funil não tiver essas colunas).
  const kanbanTourSteps = useMemo(() => {
    const steps = [];
    if (pipelines.length > 0) {
      steps.push({
        element: '[data-testid="pipeline-tabs"]',
        popover: {
          title: "Seus funis",
          description: "Troque entre funis diferentes aqui (ex: Vendas, Suporte). Cada funil tem suas próprias etapas.",
        },
      });
    }
    steps.push(
      {
        element: '[data-testid="metrics-row"]',
        popover: {
          title: "Métricas do funil",
          description: "Total de leads, em aberto, ganhos e taxa de conversão -- atualizados em tempo real conforme os leads se movem entre etapas.",
        },
      },
      {
        element: '[data-testid="kanban-board"]',
        popover: {
          title: "Arraste os cards",
          description: "Arraste um lead de uma coluna pra outra pra mudar a etapa dele. A mudança já aparece na hora, sem precisar recarregar a página.",
        },
      },
    );
    if (hasWonLostColumns) {
      steps.push({
        element: '[data-testid="toggle-won-lost"]',
        popover: {
          title: "Ganho e Perdido",
          description: "Essas colunas ficam escondidas por padrão pra manter o quadro limpo no dia a dia. Clique aqui quando quiser vê-las de novo.",
        },
      });
    }
    steps.push({
      element: '[data-testid="tag-filter-wrapper"]',
      popover: {
        title: "Filtro por tags",
        description: "Mostra só os leads que tenham pelo menos uma das tags selecionadas -- útil pra focar num grupo específico (ex: \"vip\", \"quente\").",
      },
    });
    if (isAdmin) {
      steps.push({
        element: '[data-testid="new-lead-btn"]',
        popover: {
          title: "Novo lead manual",
          description: "Além dos leads que chegam automaticamente pelo WhatsApp, você pode cadastrar um lead manualmente aqui.",
        },
      });
    }
    return steps;
  }, [pipelines.length, hasWonLostColumns, isAdmin]);

  const { start: startKanbanTour } = useProductTour("kanban", kanbanTourSteps, {
    autoStart: !loading && kanbanTourSteps.length > 0,
  });

  // ── Data fetching ────────────────────────────────────────────────────────────

  // `options.silent` evita o setLoading(true) que desmonta o KanbanBoard em
  // favor do spinner de tela cheia -- usado pra reconciliações em segundo
  // plano (WS, confirmação pós-drag) onde o estado local já foi atualizado
  // de forma otimista e essa chamada só está confirmando/sincronizando com o
  // servidor. Em modo silencioso, uma falha de rede não limpa o state atual
  // (evita apagar o kanban por causa de um hiccup passageiro) nem mostra
  // toast (evita ruído em reconciliações automáticas).
  const fetchAll = useCallback(async (pipelineId = null, options = {}) => {
    const { silent = false } = options;
    if (!silent) setLoading(true);
    try {
      const colUrl = pipelineId ? `/columns?pipeline_id=${pipelineId}` : "/columns";
      const metricsUrl = pipelineId ? `/dashboard/metrics?pipeline_id=${pipelineId}` : "/dashboard/metrics";
      // Colunas e métricas primeiro: precisamos saber quais colunas são
      // is_won/is_lost antes de montar a URL de leads (pra excluí-las da
      // carga inicial). As métricas usam sua própria query no backend
      // (GET /dashboard/metrics), então continuam contando Ganho/Perdido
      // normalmente mesmo com esses leads fora do state do kanban.
      const [c, m] = await Promise.all([api.get(colUrl), api.get(metricsUrl)]);
      const cols = Array.isArray(c.data) ? c.data : [];
      setColumns(cols);
      setMetrics(m.data || null);

      let leadUrl = pipelineId ? `/leads?pipeline_id=${pipelineId}` : "/leads";
      if (!showWonLostRef.current) {
        const hiddenIds = cols.filter((col) => col.is_won || col.is_lost).map((col) => col.id);
        if (hiddenIds.length) {
          leadUrl += (leadUrl.includes("?") ? "&" : "?") + `exclude_column_ids=${hiddenIds.join(",")}`;
        }
      }
      // Filtro de tags é independente do exclude_column_ids acima -- os dois
      // parâmetros combinam no backend (AND entre os filtros, OU dentro das
      // tags), então o toggle Ganho/Perdido continua sendo respeitado mesmo
      // com tags selecionadas.
      if (selectedTagsRef.current.length > 0) {
        leadUrl += (leadUrl.includes("?") ? "&" : "?") + `tags=${encodeURIComponent(selectedTagsRef.current.join(","))}`;
      }
      const l = await api.get(leadUrl);
      setLeads(Array.isArray(l.data) ? l.data : []);
    } catch {
      if (!silent) {
        setColumns([]);
        setLeads([]);
        toast.error("Erro ao carregar dados");
      }
    }
    if (!silent) setLoading(false);
  }, []);

  const toggleShowWonLost = () => {
    const next = !showWonLostRef.current;
    showWonLostRef.current = next;
    setShowWonLost(next);
    fetchAll(selectedPipelineRef.current?.id || null);
  };

  // Filtro de tags: seleção veio da TagFilterDropdown (dentro do Navbar).
  // É uma ação explícita do usuário, então recarrega o board normalmente
  // (com spinner) igual ao toggle Ganho/Perdido -- não é um evento de
  // background que precise ser silencioso.
  const handleTagsChange = (nextTags) => {
    const next = Array.isArray(nextTags) ? nextTags : [];
    selectedTagsRef.current = next;
    setSelectedTags(next);
    fetchAll(selectedPipelineRef.current?.id || null);
  };

  // Load pipelines once on mount, then load data for the default pipeline
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pipelines");
        const list = Array.isArray(data) ? data : [];
        setPipelines(list);
        const def = list.find((p) => p.is_default) || list[0] || null;
        selectedPipelineRef.current = def;
        setSelectedPipeline(def);
        fetchAll(def?.id || null);
      } catch {
        fetchAll(null);
      }
    })();
  }, [fetchAll]);

  // ── Realtime updates ─────────────────────────────────────────────────────────

  useEffect(() => {
    // Eventos de WebSocket são push em segundo plano -- inclusive o eco da
    // própria ação do usuário (ex: mover um card gera um lead.moved que volta
    // pra essa mesma aba). Por isso `refresh` aqui é sempre silencioso: não
    // usamos setLoading(true)/fetchAll bloqueante, que desmontava o
    // KanbanBoard e piscava a tela toda com um spinner a cada drop.
    const refresh = () => fetchAll(selectedPipelineRef.current?.id || null, { silent: true });
    const isAgentRole = user?.role === "agent";

    const u1 = on("lead.created", (p) => {
      if (isAgentRole && p?.lead && p.lead.assigned_to !== user?.id) return;
      refresh();
    });

    const u2 = on("lead.updated", (p) => {
      const updatedLead = p?.lead;
      if (isAgentRole && updatedLead) {
        if (updatedLead.assigned_to !== user?.id) {
          // Lead was reassigned away from this agent - remove immediately
          setLeads((prev) =>
            (Array.isArray(prev) ? prev : []).filter((l) => l.id !== updatedLead.id)
          );
          if (activeLead?.id === updatedLead.id) setActiveLead(null);
          return;
        }
      }

      if (updatedLead?.id) {
        setLeads((prev) =>
          (Array.isArray(prev) ? prev : []).map((lead) =>
            lead.id === updatedLead.id ? { ...lead, ...updatedLead, name: updatedLead.name || lead.name } : lead
          )
        );
        if (activeLead?.id === updatedLead.id) {
          setActiveLead((prev) => (prev ? { ...prev, ...updatedLead, name: updatedLead.name || prev.name } : updatedLead));
        }
      }
      refresh();
    });

    const u3 = on("lead.moved", refresh);
    const u4 = on("lead.lost", refresh);
    const u5 = on("message.received", (p) => {
      setLeads((prev) =>
        (Array.isArray(prev) ? prev : []).map((l) =>
          l.id === p.lead_id
            ? {
                ...l,
                last_message_preview: p.message.content,
                last_message_at: p.message.created_at,
                last_message_media_url: p.message.media_url,
                last_message_media_type: p.message.media_type,
              }
            : l
        )
      );
    });
    return () => { u1?.(); u2?.(); u3?.(); u4?.(); u5?.(); };
  }, [on, fetchAll, user, activeLead]);

  // ── Event handlers ────────────────────────────────────────────────────────────

  const handlePipelineChange = (pipeline) => {
    selectedPipelineRef.current = pipeline;
    setSelectedPipeline(pipeline);
    fetchAll(pipeline?.id || null);
  };

  const handleCardClick = (lead) => setActiveLead(lead);
  const handleLeadUpdate = (updated) => {
    if (user?.role === "agent" && updated?.assigned_to !== user?.id) {
      setLeads((prev) => (Array.isArray(prev) ? prev : []).filter((l) => l.id !== updated.id));
      if (activeLead?.id === updated.id) setActiveLead(null);
      fetchAll(selectedPipelineRef.current?.id || null, { silent: true });
      return;
    }
    setLeads((prev) => (Array.isArray(prev) ? prev : []).map((l) => (l.id === updated.id ? { ...l, ...updated, name: updated.name || l.name } : l)));
    if (activeLead?.id === updated.id) setActiveLead((prev) => (prev ? { ...prev, ...updated, name: updated.name || prev.name } : updated));
    // Reconciliação em segundo plano (sem spinner de tela cheia) -- o state
    // local já foi atualizado acima (e, no caso de drag-and-drop, já tinha
    // sido atualizado de forma otimista antes mesmo do PATCH terminar). Essa
    // chamada só garante que colunas/métricas fiquem 100% consistentes com o
    // servidor, sem travar a UI.
    fetchAll(selectedPipelineRef.current?.id || null, { silent: true });
  };

  // Move um lead de coluna localmente, sem chamada de rede -- usado pelo
  // drag-and-drop do kanban pra já mostrar o card na coluna nova assim que o
  // usuário solta (ou confirma a perda), e também pra reverter caso o PATCH
  // em segundo plano falhe (chamado de novo com origem/destino trocados).
  // Recalcula as métricas do topo localmente a partir das flags is_won/
  // is_lost das colunas envolvidas, sem precisar refazer a query do backend.
  const handleLeadMoveOptimistic = useCallback((leadId, newColumnId, oldColumnId) => {
    setLeads((prev) =>
      (Array.isArray(prev) ? prev : []).map((l) => (l.id === leadId ? { ...l, column_id: newColumnId } : l))
    );
    setActiveLead((prev) => (prev && prev.id === leadId ? { ...prev, column_id: newColumnId } : prev));
    setMetrics((prev) => {
      if (!prev) return prev;
      const fromCol = safeColumns.find((c) => c.id === oldColumnId);
      const toCol = safeColumns.find((c) => c.id === newColumnId);
      if (!fromCol || !toCol) return prev;
      const lead = safeLeads.find((l) => l.id === leadId);
      const value = Number(lead?.value) || 0;
      let wonCount = prev.won_count || 0;
      let lostCount = prev.lost_count || 0;
      let wonValue = prev.won_value || 0;
      if (fromCol.is_won) { wonCount -= 1; wonValue -= value; }
      if (fromCol.is_lost) lostCount -= 1;
      if (toCol.is_won) { wonCount += 1; wonValue += value; }
      if (toCol.is_lost) lostCount += 1;
      wonCount = Math.max(0, wonCount);
      lostCount = Math.max(0, lostCount);
      wonValue = Math.max(0, wonValue);
      const total = prev.total_leads || 0;
      const openCount = Math.max(0, total - wonCount - lostCount);
      const conversionRate = total ? Math.round((wonCount / total) * 1000) / 10 : 0;
      return {
        ...prev,
        won_count: wonCount,
        lost_count: lostCount,
        won_value: Math.round(wonValue * 100) / 100,
        open_count: openCount,
        conversion_rate: conversionRate,
      };
    });
  }, [safeColumns, safeLeads]);
  const handleLeadDelete = (leadId) => {
    setLeads((prev) => (Array.isArray(prev) ? prev : []).filter((lead) => lead.id !== leadId));
    if (activeLead?.id === leadId) setActiveLead(null);
    setMetrics((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        total_leads: Math.max(0, (prev.total_leads || 0) - 1),
        open_count: Math.max(0, (prev.open_count || 0) - 1),
      };
    });
  };

  const handlePipelineCreated = (pipeline) => {
    setPipelines((prev) => [...prev, pipeline]);
    handlePipelineChange(pipeline);
  };

  const handlePipelineRenamed = async (pipelineId, newName) => {
    try {
      const { data } = await api.patch(`/pipelines/${pipelineId}`, { name: newName });
      setPipelines((prev) => prev.map((p) => (p.id === pipelineId ? { ...p, name: data.name } : p)));
      if (selectedPipeline?.id === pipelineId) {
        setSelectedPipeline((prev) => ({ ...prev, name: data.name }));
      }
      setRenamePipeline(null);
      toast.success("Funil renomeado");
    } catch {
      toast.error("Erro ao renomear funil");
    }
  };

  const handlePipelineDelete = async (pipelineId) => {
    if (!window.confirm("Excluir este funil? Apenas funis sem leads ativos podem ser excluídos.")) return;
    try {
      await api.delete(`/pipelines/${pipelineId}`);
      const next = pipelines.filter((p) => p.id !== pipelineId);
      setPipelines(next);
      if (selectedPipeline?.id === pipelineId) {
        const def = next.find((p) => p.is_default) || next[0] || null;
        handlePipelineChange(def);
      }
      toast.success("Funil excluído");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir funil");
    }
  };

  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden">
      <Sidebar />
      <main className="flex-1 flex flex-col min-w-0">
        <Navbar
          title={selectedPipeline?.name || "Funil de Vendas"}
          onOpenLeadFromNotification={setActiveLead}
          showTagFilter
          selectedTags={selectedTags}
          onTagsChange={handleTagsChange}
          subtitle={
            isAdmin
              ? "Gerencie leads, converse em tempo real e feche mais negócios"
              : `Mostrando apenas os leads atribuídos a você · ${leads.length} lead${leads.length !== 1 ? "s" : ""}`
          }
          actions={
            isAdmin ? (
              <Button
                data-testid="new-lead-btn"
                onClick={() => setNewLeadOpen(true)}
                className="bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold"
              >
                <Plus className="w-4 h-4 mr-1.5" /> Novo Lead
              </Button>
            ) : null
          }
        />

        {/* ── Pipeline selector tabs ────────────────────────────────────────── */}
        {pipelines.length > 0 && (
          <div data-testid="pipeline-tabs" className="px-6 pt-4 flex items-center gap-2 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
            {pipelines.map((p) => (
              <div key={p.id} className="group relative flex items-center">
                <button
                  onClick={() => handlePipelineChange(p)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition whitespace-nowrap ${
                    selectedPipeline?.id === p.id
                      ? "bg-[#25D366] text-slate-950"
                      : "bg-slate-900 border border-slate-800 text-slate-300 hover:border-slate-600 hover:text-slate-100"
                  }`}
                >
                  {p.name}
                </button>
                {/* Admin controls shown on hover */}
                {isAdmin && (
                  <div className="absolute -top-1 -right-1 hidden group-hover:flex items-center gap-0.5 bg-slate-900 border border-slate-700 rounded-md p-0.5 z-10">
                    <button
                      onClick={(e) => { e.stopPropagation(); setRenamePipeline({ id: p.id, name: p.name }); }}
                      className="p-1 text-slate-400 hover:text-[#25D366] rounded transition"
                      title="Renomear funil"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    {!p.is_default && !p.is_system && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePipelineDelete(p.id); }}
                        className="p-1 text-slate-400 hover:text-red-400 rounded transition"
                        title="Excluir"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {isAdmin && (
              <button
                onClick={() => setNewPipelineOpen(true)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border border-dashed border-slate-700 text-slate-500 hover:border-[#25D366]/60 hover:text-[#25D366] transition whitespace-nowrap flex items-center gap-1.5 shrink-0"
              >
                <Plus className="w-3.5 h-3.5" /> Novo Funil
              </button>
            )}
          </div>
        )}

        {/* ── Metrics ──────────────────────────────────────────────────────── */}
        {metrics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-6 pt-4" data-testid="metrics-row">
            <MetricCard testid="metric-total" icon={Users} label="Total de Leads"
              value={metrics.total_leads} accent="bg-slate-700/40 text-slate-200" />
            <MetricCard testid="metric-open" icon={TrendingUp} label="Em Aberto"
              value={metrics.open_count} accent="bg-blue-500/15 text-blue-400" />
            <MetricCard testid="metric-won" icon={Trophy} label="Ganhos"
              value={`${metrics.won_count} · R$ ${metrics?.won_value?.toLocaleString?.("pt-BR") || "0"}`}
              accent="bg-[#25D366]/15 text-[#25D366]" />
            <MetricCard testid="metric-conversion" icon={Percent} label="Conversão"
              value={`${metrics.conversion_rate}%`} accent="bg-purple-500/15 text-purple-400" />
          </div>
        )}

        {/* ── Kanban ───────────────────────────────────────────────────────── */}
        {hasWonLostColumns && (
          <div className="px-6 pt-3 flex justify-end items-center gap-1.5">
            <button
              data-testid="toggle-won-lost"
              onClick={toggleShowWonLost}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-700 bg-slate-900 text-slate-300 hover:border-[#25D366]/60 hover:text-slate-100 transition"
            >
              {showWonLost ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {showWonLost ? "Ocultar Ganho e Perdido" : "Mostrar Ganho e Perdido"}
            </button>
            <HelpTooltip text="Por padrão, leads Ganhos e Perdidos ficam escondidos do quadro pra deixar o funil mais limpo no dia a dia. Ative aqui pra ver essas colunas de novo -- os leads continuam contando normalmente nos números do topo, mesmo escondidos." />
          </div>
        )}
        <div className="flex-1 overflow-hidden px-6 pt-5">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-10 h-10 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <KanbanBoard
              columns={safeColumns}
              hiddenColumnIds={hiddenColumnIds}
              leads={safeLeads}
              onCardClick={handleCardClick}
              onLeadUpdate={handleLeadUpdate}
              onLeadMoveOptimistic={handleLeadMoveOptimistic}
              onColumnsChange={(cols) => setColumns(Array.isArray(cols) ? cols : [])}
              onColumnCreate={async (data) => {
                try {
                  const { data: col } = await api.post("/columns", {
                    ...data,
                    pipeline_id: selectedPipelineRef.current?.id || null,
                  });
                  setColumns((prev) => [...(Array.isArray(prev) ? prev : []), col]);
                  toast.success("Etapa criada");
                } catch {
                  toast.error("Erro ao criar etapa");
                }
              }}
            />
          )}
        </div>
      </main>

      <ChatPanel
        lead={activeLead}
        onClose={() => setActiveLead(null)}
        onLeadUpdate={handleLeadUpdate}
        onLeadDelete={handleLeadDelete}
        initialPrefillMessage={pendingPrefillMessage}
        onInitialPrefillConsumed={handleInitialPrefillConsumed}
      />

      <NewLeadDialog
        open={newLeadOpen}
        columns={safeColumns}
        selectedPipeline={selectedPipeline}
        onClose={() => setNewLeadOpen(false)}
        onCreated={(lead) => {
          setLeads((prev) => [lead, ...(Array.isArray(prev) ? prev : [])]);
          fetchAll(selectedPipelineRef.current?.id || null);
        }}
      />

      <NewPipelineDialog
        open={newPipelineOpen}
        onClose={() => setNewPipelineOpen(false)}
        onCreated={handlePipelineCreated}
      />

      <RenamePipelineDialog
        pipeline={renamePipeline}
        onClose={() => setRenamePipeline(null)}
        onSaved={handlePipelineRenamed}
      />

      {!loading && kanbanTourSteps.length > 0 && (
        <TourRestartButton onClick={startKanbanTour} label="Rever tour do Kanban" />
      )}
    </div>
  );
}

// ── New Lead Dialog ───────────────────────────────────────────────────────────

function NewLeadDialog({ open, onClose, columns, selectedPipeline, onCreated }) {
  const [form, setForm] = useState({
    name: "", company: "", phone: "", email: "", channel: "whatsapp",
    source_tag: "", value: 0, column_id: "", assigned_to: "",
  });
  const [team, setTeam] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/team").then(({ data }) => setTeam(Array.isArray(data) ? data : [])).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (open && columns?.length && !form.column_id) {
      // Default to "Novo Lead" column (system_key = new_lead) or first column
      const newLeadCol = columns.find((c) => c.system_key === "new_lead") || columns[0];
      setForm((f) => ({ ...f, column_id: newLeadCol?.id || "" }));
    }
  }, [open, columns, form.column_id]);

  const submit = async () => {
    if (!form.name || !form.column_id) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        value: Number(form.value) || 0,
        pipeline_id: selectedPipeline?.id || null,
        assigned_to: form.assigned_to || null,
      };
      const { data } = await api.post("/leads", payload);
      toast.success("Lead criado!");
      onCreated(data);
      onClose();
      setForm({ name: "", company: "", phone: "", email: "", channel: "whatsapp",
        source_tag: "", value: 0, column_id: "", assigned_to: "" });
    } catch {
      toast.error("Erro ao criar lead");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="new-lead-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>
            Novo Lead{selectedPipeline ? ` — ${selectedPipeline.name}` : ""}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-xs text-slate-400">Nome *</Label>
            <Input data-testid="new-lead-name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="bg-slate-800 border-slate-700 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Empresa</Label>
            <Input data-testid="new-lead-company" value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="bg-slate-800 border-slate-700 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Telefone</Label>
            <Input data-testid="new-lead-phone" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="bg-slate-800 border-slate-700 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Email</Label>
            <Input data-testid="new-lead-email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-slate-800 border-slate-700 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Etapa *</Label>
            <select
              data-testid="new-lead-column"
              value={form.column_id}
              onChange={(e) => setForm({ ...form, column_id: e.target.value })}
              className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-sm text-slate-100 outline-none focus:border-[#25D366]"
            >
              {columns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Canal</Label>
            <select
              data-testid="new-lead-channel"
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })}
              className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-sm text-slate-100 outline-none focus:border-[#25D366]"
            >
              {["whatsapp","instagram","email","facebook","webchat","manual"].map((ch) => (
                <option key={ch} value={ch}>{ch}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Valor (R$)</Label>
            <Input type="number" value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              className="bg-slate-800 border-slate-700 mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Origem</Label>
            <Input value={form.source_tag}
              onChange={(e) => setForm({ ...form, source_tag: e.target.value })}
              className="bg-slate-800 border-slate-700 mt-1" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-slate-400">Responsável</Label>
            <p className="text-[11px] text-slate-500 mt-1">
              Opcional. Sem responsável, o lead fica como entrada do funil até ser atribuído.
            </p>
            <select
              value={form.assigned_to}
              onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
              className="w-full mt-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-sm text-slate-100 outline-none focus:border-[#25D366]"
            >
              <option value="">— Nenhum —</option>
              {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter className="gap-2 mt-2">
          <Button variant="ghost" onClick={onClose} className="text-slate-300 hover:bg-slate-800">Cancelar</Button>
          <Button
            data-testid="new-lead-submit"
            onClick={submit}
            disabled={busy || !form.name.trim() || !form.column_id}
            className="bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold"
          >
            {busy ? "Criando..." : "Criar Lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── New Pipeline Dialog ───────────────────────────────────────────────────────

function NewPipelineDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/pipelines", { name: name.trim() });
      toast.success(`Funil "${data.name}" criado com etapas padrão`);
      onCreated(data);
      onClose();
      setName("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar funil");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-sm">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Novo Funil</DialogTitle>
        </DialogHeader>
        <div className="mt-2">
          <Label className="text-xs text-slate-400">Nome do funil *</Label>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="ex: Marketing, Renovação..."
            className="bg-slate-800 border-slate-700 mt-1"
          />
          <p className="text-[11px] text-slate-500 mt-2">
            Serão criadas automaticamente as etapas: Novo Lead, Ganho e Perdido.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="text-slate-300 hover:bg-slate-800">Cancelar</Button>
          <Button
            onClick={submit}
            disabled={busy || !name.trim()}
            className="bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold"
          >
            {busy ? "Criando..." : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Rename Pipeline Dialog ────────────────────────────────────────────────────

function RenamePipelineDialog({ pipeline, onClose, onSaved }) {
  const [name, setName] = useState("");
  useEffect(() => { if (pipeline) setName(pipeline.name); }, [pipeline]);

  const submit = () => {
    if (!name.trim()) return;
    onSaved(pipeline.id, name.trim());
  };

  return (
    <Dialog open={!!pipeline} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-sm">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Renomear Funil</DialogTitle>
        </DialogHeader>
        <div className="mt-2">
          <Label className="text-xs text-slate-400">Novo nome</Label>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            className="bg-slate-800 border-slate-700 mt-1"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="text-slate-300 hover:bg-slate-800">Cancelar</Button>
          <Button
            onClick={submit}
            disabled={!name.trim()}
            className="bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold"
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default Dashboard;
